/**
 * Live Search / Predictive Search
 * Displays autocomplete results as user types
 */

(function() {
  'use strict';

  class LiveSearch {
    constructor(searchForm, searchInput) {
      this.searchInput = searchInput;
      this.searchForm = searchForm;
      this.resultsContainer = null;
      this.debounceTimer = null;
      this.minChars = 2;
      this.currentQuery = '';

      this.init();
    }

    init() {
      if (!this.searchInput) return;

      // Create results container
      this.createResultsContainer();

      // Add event listeners
      this.searchInput.addEventListener('input', (e) => this.handleInput(e));
      this.searchInput.addEventListener('focus', (e) => this.handleFocus(e));

      // Close results when clicking outside
      document.addEventListener('click', (e) => {
        if (!this.searchForm.contains(e.target)) {
          this.hideResults();
        }
      });

      // Prevent form submission, allow Enter to go to first result
      this.searchForm.addEventListener('submit', (e) => {
        if (this.resultsContainer && !this.resultsContainer.classList.contains('hide')) {
          const firstLink = this.resultsContainer.querySelector('.live-search-result__link');
          if (firstLink) {
            e.preventDefault();
            window.location.href = firstLink.href;
          }
        }
      });
    }

    createResultsContainer() {
      this.resultsContainer = document.createElement('div');
      this.resultsContainer.className = 'live-search-results hide';
      this.searchForm.appendChild(this.resultsContainer);
    }

    handleInput(event) {
      const query = event.target.value.trim();

      // Clear previous timer
      clearTimeout(this.debounceTimer);

      // If query is too short, hide results
      if (query.length < this.minChars) {
        this.hideResults();
        return;
      }

      // Debounce the search
      this.debounceTimer = setTimeout(() => {
        this.performSearch(query);
      }, 300);
    }

    handleFocus(event) {
      const query = event.target.value.trim();
      if (query.length >= this.minChars && this.currentQuery === query) {
        this.showResults();
      }
    }

    performSearch(query) {
      this.currentQuery = query;

      // Show loading state
      this.showLoading();

      // Use Shopify's predictive search API
      const searchUrl = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor,tag`;

      fetch(searchUrl)
        .then(response => response.json())
        .then(data => {
          this.displayResults(data, query);
        })
        .catch(error => {
          console.error('Search error:', error);
          this.hideResults();
        });
    }

    showLoading() {
      this.resultsContainer.innerHTML = `
        <div class="live-search-loading">
          <div class="live-search-spinner"></div>
          <p>Searching...</p>
        </div>
      `;
      this.showResults();
    }

    displayResults(data, query) {
      const products = data.resources.results.products || [];

      if (products.length === 0) {
        this.resultsContainer.innerHTML = `
          <div class="live-search-empty">
            <p>No products found for "${this.escapeHtml(query)}"</p>
          </div>
        `;
        this.showResults();
        return;
      }

      let html = '<div class="live-search-results__list">';

      products.forEach(product => {
        const image = product.image ? product.image : '';
        const available = product.available ? '' : '<span class="live-search-result__badge">Sold Out</span>';

        html += `
          <a href="${product.url}" class="live-search-result__link">
            <div class="live-search-result">
              <div class="live-search-result__image">
                ${image ? `<img src="${image}" alt="${this.escapeHtml(product.title)}" loading="lazy">` : '<div class="live-search-result__image-placeholder"></div>'}
              </div>
              <div class="live-search-result__details">
                <div class="live-search-result__title">${this.highlightMatch(product.title, query)}</div>
                <div class="live-search-result__vendor">${product.vendor || ''}</div>
                ${available}
              </div>
            </div>
          </a>
        `;
      });

      html += '</div>';

      // Add "View all results" link
      html += `
        <div class="live-search-footer">
          <a href="/search?q=${encodeURIComponent(query)}&type=product" class="live-search-view-all">
            View all results for "${this.escapeHtml(query)}"
          </a>
        </div>
      `;

      this.resultsContainer.innerHTML = html;
      this.showResults();
    }

    highlightMatch(text, query) {
      const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');
      return this.escapeHtml(text).replace(regex, '<mark>$1</mark>');
    }

    showResults() {
      if (this.resultsContainer) {
        this.resultsContainer.classList.remove('hide');
      }
    }

    hideResults() {
      if (this.resultsContainer) {
        this.resultsContainer.classList.add('hide');
      }
    }

    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, Shopify.money_format || '${{amount}}');
      }
      return '$' + (cents / 100).toFixed(2);
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    escapeRegex(text) {
      return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
  }

  // Initialize when DOM is ready
  function initLiveSearch() {
    const searchForms = document.querySelectorAll('.header-search-form');
    searchForms.forEach(form => {
      const input = form.querySelector('.header-search-input');
      if (input) {
        new LiveSearch(form, input);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLiveSearch);
  } else {
    initLiveSearch();
  }
})();
